import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import type { SubscriptionFeatures } from '@busswerks/shared'

export type SubscriptionTier = 'free' | 'pro' | 'studio'

export interface User {
  id: string
  email: string
  name?: string
  avatar?: string
  teamId?: string
  /**
   * The user's current subscription tier, re-derived from the
   * shared TIER_FEATURES table on every auth check. Source of
   * truth — use this instead of `subscription.tier` when gating
   * UI, since it can't drift from the canonical tier definitions.
   */
  tier: SubscriptionTier
  /**
   * Feature flags + limits for the current tier. Pulled from
   * TIER_FEATURES by the backend at auth time, so any change to
   * the shared constants takes effect on the next auth check
   * without needing to replay historical webhooks.
   */
  features: SubscriptionFeatures
  subscription?: {
    tier: SubscriptionTier
    status: 'active' | 'cancelled' | 'past_due' | 'trialing'
    currentPeriodStart?: string
    currentPeriodEnd?: string
    cancelledAt?: string
    /**
     * True when the user has an active Stripe subscription row.
     * Drives whether the upgrade button shows "Upgrade" (false)
     * vs "Manage billing" (true).
     */
    hasActiveSubscription?: boolean
  }
}

export function useAuth() {
  const {
    data: user,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      try {
        return await apiClient.get<User>('/api/v1/users/me')
      } catch {
        return null
      }
    },
    // Tight staleTime + refetch on focus/reconnect so the auth
    // state matches reality as soon as the user navigates back
    // to a tab where they recently logged in. Previously this was
    // 5 minutes, which meant the home page kept showing the Login
    // button for up to 5 minutes after the user authenticated on
    // /login → /home. 30s is short enough to feel live and
    // long enough to avoid hammering the server during navigation.
    staleTime: 30 * 1000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    retry: false,
  })

  return {
    user: user ?? null,
    isLoading,
    isAuthenticated: !!user,
    error,
  }
}

/**
 * Convenience hook for tier-gated UI. Returns `true` if the
 * user's feature flag for the given name is truthy (handles
 * booleans like aiSuggestions/cloudSync/teamSharing). For
 * numeric limits (maxLayouts, maxActiveShares) fall back to
 * reading `user.features[key]` directly.
 */
export function useFeatureFlag(key: keyof SubscriptionFeatures): boolean {
  const { user } = useAuth()
  if (!user) return false
  const value = user.features[key]
  return typeof value === 'boolean' ? value : Boolean(value)
}
